import React from "react";
import {
  ChannelPreviewUIComponentProps,
  useChatContext,
} from "stream-chat-react";
import { useNavigate } from "react-router-dom";
import { useReservation } from "@/hooks/useReservations";
import ChannelUserAvatar from "@/components/pages/chats/component/chat-custom-components/ChannelUserAvatar";
import { Reservation } from "@/types/reservation";
import { getShortChatDate } from "@/utils/dateUtils";
import { Paperclip, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequestPreview } from "@/components/pages/chats/component/chat-custom-components/RequestPreview";
import { cn } from "@/lib/utils";
import { dashboardNestedCardClassName } from "@/components/pages/dashboard/components/DashboardCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import "./DashboardChartListItem.css";

export const DashboardChatListItem = (
  props: ChannelPreviewUIComponentProps,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useChatContext();
  const { channel, lastMessage, unread } = props;

  const currentUserId = client.userID;

  const chatType = channel?.data?.chat_type;
  const isGroup = chatType === "group";

  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== currentUserId,
  )?.user;

  const activeUserName = isGroup
    ? channel.data?.name
    : otherMember?.name ||
      lastMessage?.user?.name ||
      channel.data?.created_by?.name ||
      t("chats.emptyChat");

  const bookingId =
    channel?.data?.bookingId || channel?.data?.metadata?.bookingId;

  const { data: channelRelatedBooking } =
    useReservation<Reservation>(bookingId);

  const bookingRoom = channelRelatedBooking?.room;

  const roomSubtitle =
    bookingRoom?.category?.pmsType?.trim() ||
    bookingRoom?.category?.name?.trim() ||
    "";

  const attachmentCount = lastMessage?.attachments?.length || 0;
  const isOnlyAttachmentMessage = !lastMessage?.text && attachmentCount > 0;

  const renderMessagePreview = () => {
    if (lastMessage?.text) {
      return lastMessage.text;
    }

    if (isOnlyAttachmentMessage) {
      return (
        <span className="flex items-center gap-1 font-medium text-blue-600">
          <Paperclip className="size-3" />
          {attachmentCount}{" "}
          {attachmentCount === 1
            ? t("chats.attachment")
            : t("chats.attachments")}{" "}
          {t("chats.sent")}
        </span>
      );
    }

    if (lastMessage?.requestId) {
      return null;
    }
    return (
      <span className="text-slate-400 italic">{t("chats.emptyChat")}</span>
    );
  };

  const openChannel = () => {
    const channelId = channel.id;
    if (channelId) {
      navigate(`/chats?activeChannel=${encodeURIComponent(channelId)}`);
    }
  };

  return (
    <Button
      variant="new_default"
      onClick={openChannel}
      className={cn(
        dashboardNestedCardClassName(
          "border-border mb-2 h-fit w-full flex-row gap-3 bg-neutral-50 px-2 py-2.5 text-left transition-colors hover:bg-zinc-50",
        ),
      )}
    >
      <div className="relative shrink-0">
        <ChannelUserAvatar
          name={activeUserName}
          className="size-8 rounded-lg"
        />
        {!!unread && unread > 0 && (
          <div className="bg-royal-glow-3 absolute -top-0.5 -right-0.5 size-2.5 rounded-full" />
        )}
        {bookingRoom?.number != null ? (
          <Badge
            variant="service_gray_sm"
            className="bg-monochrome-core-2 absolute top-1/2 right-1/2 max-h-4 w-fit translate-x-1/2 translate-y-1/2 text-white"
          >
            {bookingRoom.number}
          </Badge>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
        <div className="flex w-full min-w-0 flex-row items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <span className="text-eathy-comfort-1 max-w-[45%] shrink-0 truncate text-base font-semibold">
              {activeUserName}
            </span>
            {roomSubtitle ? (
              <span className="text-eathy-comfort-2 min-w-0 flex-1 truncate text-sm font-normal">
                {roomSubtitle}
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-row items-center gap-2">
            <Smartphone
              className="text-eathy-comfort-2 size-3.5 shrink-0"
              aria-hidden
            />
            {lastMessage?.updated_at ? (
              <span className="text-xs whitespace-nowrap text-neutral-500">
                {getShortChatDate(lastMessage.updated_at)}
              </span>
            ) : null}
          </div>
        </div>
        <p className="truncate text-base font-normal whitespace-nowrap text-[#171717]">
          {renderMessagePreview()}
        </p>
        {lastMessage?.requestId ? (
          <RequestPreview requestId={lastMessage.requestId} />
        ) : null}
      </div>
    </Button>
  );
};
